/**
 * Reporting data contract.
 *
 * These types are the boundary between the reporting UI and its data source.
 * The dashboard only ever consumes de-identified AGGREGATES — never raw whānau
 * rows — because whānau data is sensitive (see CLAUDE.md "Data, identity, and
 * security"). In production the aggregates are computed server-side by the WFP
 * API and returned from GET /api/v1/reports/aggregates; in dev they are computed
 * from the mock profiles by the same pure functions in ./aggregate.ts so the
 * numbers are identical and unit-testable.
 *
 * `ReportProfile` is the DEV-ONLY per-whānau shape used to drive the mock
 * aggregation. It never crosses the network in production.
 */
import type {
  FactorStateKind,
  PouId,
  DomainId,
  SubdomainId,
} from "../framework/types";

// -------- Cohort dimensions --------

export type DurationBand = "under-6m" | "6-12m" | "1-2y" | "over-2y";

export const DURATION_BAND_ORDER: readonly DurationBand[] = [
  "under-6m",
  "6-12m",
  "1-2y",
  "over-2y",
] as const;

/** The filterable cohort dimensions. Empty array on a dimension = no filter. */
export type CohortFilters = {
  location: string[];
  iwi: string[];
  ethnicity: string[];
  service: string[];
  durationBand: DurationBand[];
};

export type CohortDimension = keyof CohortFilters;

export const EMPTY_FILTERS: CohortFilters = {
  location: [],
  iwi: [],
  ethnicity: [],
  service: [],
  durationBand: [],
};

export const COHORT_DIMENSIONS: readonly CohortDimension[] = [
  "location",
  "iwi",
  "ethnicity",
  "service",
  "durationBand",
];

// -------- Dev-only per-whānau shape (mock aggregation input) --------

export type ReportProfile = {
  id: number;
  household: number | null;
  ethnicity: string | null;
  iwi: string | null;
  location: string | null;
  service: string | null;
  durationBand: DurationBand | null;
  /** Factor state per subdomain at first assessment. Missing id = not-assessed. */
  entry: Record<SubdomainId, FactorStateKind>;
  /** Factor state per subdomain at exit / latest. Missing id = not-assessed. */
  exit: Record<SubdomainId, FactorStateKind>;
};

export type Phase = "entry" | "exit";

// -------- Aggregate building blocks --------

/** Raw counts of each state within a group, for one phase. */
export type StateCounts = {
  risk: number;
  change: number;
  protective: number;
  notAssessed: number;
};

/** Proportions over the ASSESSED total (risk + change + protective). Sums to 1. */
export type StateProportion = {
  risk: number;
  change: number;
  protective: number;
};

/** A whānau's single dominant state (ADR-010). */
export type WhanauState =
  | "risk"
  | "change"
  | "protective"
  | "mixed"
  | "unassessed";

/** Distribution of whānau across their dominant state (ADR-010). Sums to total. */
export type WhanauStateDistribution = {
  risk: number;
  change: number;
  protective: number;
  mixed: number;
  unassessed: number;
  total: number;
};

/** Entry→exit transition of whānau-level state: transition[from][to] = count. */
export type WhanauStateTransition = Record<
  WhanauState,
  Record<WhanauState, number>
>;

/** Whānau-level dominant state at entry vs exit, plus the transition (ADR-010). */
export type WhanauStateEntryExit = {
  entry: WhanauStateDistribution;
  exit: WhanauStateDistribution;
  transition: WhanauStateTransition;
  total: number;
};

/** Entry vs exit distribution for a subdomain or roll-up group. */
export type EntryExit = {
  entry: StateCounts;
  exit: StateCounts;
  entryProportion: StateProportion;
  exitProportion: StateProportion;
  /** Assessed denominator at each phase (excludes not-assessed). */
  assessedEntry: number;
  assessedExit: number;
};

/**
 * Entry × exit transition counts among whānau assessed (risk/change/protective)
 * at BOTH phases. Indices: 0 = risk, 1 = change, 2 = protective.
 * matrix[entryIndex][exitIndex].
 */
export type TransitionMatrix = {
  matrix: number[][];
  /** Total whānau assessed at both phases (= sum of all cells). */
  total: number;
};

export type ChangeCategory =
  | "remainedRisk"
  | "remainedChange"
  | "remainedProtective"
  | "riskToChange"
  | "riskToProtective"
  | "changeToProtective"
  | "regressed";

export const CHANGE_CATEGORY_ORDER: readonly ChangeCategory[] = [
  "riskToProtective",
  "riskToChange",
  "changeToProtective",
  "remainedProtective",
  "remainedChange",
  "remainedRisk",
  "regressed",
] as const;

/**
 * "Analysis of change" over instances assessed at BOTH phases.
 * `improved`/`remained`/`regressed` are convenience roll-ups of the categories.
 */
export type ChangeAnalysis = {
  counts: Record<ChangeCategory, number>;
  proportion: Record<ChangeCategory, number>;
  total: number;
  improved: number;
  remained: number;
  regressed: number;
};

// -------- Grouped aggregates --------

export type SubdomainAggregate = EntryExit & {
  subdomainId: SubdomainId;
  pouId: PouId;
  domainId: DomainId;
  transition: TransitionMatrix;
  analysis: ChangeAnalysis;
};

export type PouAggregate = EntryExit & {
  pouId: PouId;
  analysis: ChangeAnalysis;
};

export type DomainAggregate = EntryExit & {
  domainId: DomainId;
  analysis: ChangeAnalysis;
};

/** One selectable value within a cohort dimension, with its whānau count. */
export type Facet = {
  value: string;
  count: number;
};

/**
 * The full payload the dashboard renders. Everything here is de-identified —
 * counts and proportions only.
 */
export type ReportAggregates = {
  /** Whānau in the current (filtered) cohort. */
  totalWhanau: number;
  /** True when any cohort filter is active. */
  filtered: boolean;
  /**
   * True when the filtered cohort is below the server's minimum reporting
   * threshold; the figures are withheld to avoid re-identifying small cohorts.
   * The facets are still returned so the user can widen the filter.
   */
  suppressed?: boolean;
  /** Whole-cohort roll-up across every subdomain. */
  population: EntryExit & { analysis: ChangeAnalysis };
  bySubdomain: SubdomainAggregate[];
  byPou: PouAggregate[];
  byDomain: DomainAggregate[];
  /** Whānau-level dominant state at entry vs exit + transition (ADR-010). */
  whanauState: WhanauStateEntryExit;
  /** Available filter values + counts, computed over the UNFILTERED population. */
  facets: Record<CohortDimension, Facet[]>;
  /** ISO timestamp the aggregates were computed. */
  generatedAt: string;
};
